import { useMemo } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// AxisRadarChart — lecture exacte de la position sur N axes simultanément.
//
// Pourquoi ce composant existe : dans la scène 3D, position6D → 3D ne projette
// vraiment que 3 axes (posture, temps, autre) sur les 3 coordonnées principales ;
// les 3 autres (mesure, erreur, systeme) ne sont que des micro-décalages à 0.4×
// l'échelle, donc quasi invisibles à l'œil. Ce radar affiche les 6 valeurs
// exactes, côte à côte, sans perte — et peut superposer une seconde position
// (typiquement l'archétype le plus proche) pour visualiser l'écart axe par axe.
//
// Convention : rayon 0 (centre) = pôle A de l'axe, rayon 1 (bord) = pôle B.
// ═══════════════════════════════════════════════════════════════════════════════

const polarPoint = (cx, cy, r, angleDeg) => {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const toPointsAttr = (pts) => pts.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

export const AxisRadarChart = ({
  axes = [],
  position = {},
  comparePosition = null,
  compareColor = '#a78bfa',
  size = 200,
  fillColor = '#22d3ee',
  labelColor = '#7c8098',
  gridColor = 'rgba(167,139,250,0.14)',
}) => {
  const n = axes.length;
  // ✅ `size` est le diamètre du tracé (polygone + grille), pas celui du SVG.
  // Avant ce correctif, labelR valait 0.46×size avec overflow:visible : les
  // libellés diagonaux (4 axes sur 6 ne sont ni parfaitement en haut ni en
  // bas) dépassaient la largeur du SVG, donc du cadre du panneau qui l'englobe
  // — visuellement coupés / hors du fond. On réserve maintenant une marge
  // dédiée (PAD) autour du tracé, incluse dans les dimensions réelles du SVG,
  // pour que le texte ait physiquement la place d'exister dans la boîte.
  const PAD = size * 0.26;
  const totalSize = size + PAD * 2;
  const cx = totalSize / 2;
  const cy = totalSize / 2;
  const maxR = size * 0.34;
  const labelR = maxR + PAD * 0.62;
  const angleStep = n > 0 ? 360 / n : 0;

  const mainPoints = useMemo(() => axes.map((axis, i) => {
    const v = Math.max(0, Math.min(1, position?.[axis.id] ?? 0.5));
    return polarPoint(cx, cy, maxR * v, i * angleStep);
  }), [axes, position, angleStep, cx, cy, maxR]);

  const comparePoints = useMemo(() => {
    if (!comparePosition) return null;
    return axes.map((axis, i) => {
      const v = Math.max(0, Math.min(1, comparePosition?.[axis.id] ?? 0.5));
      return polarPoint(cx, cy, maxR * v, i * angleStep);
    });
  }, [axes, comparePosition, angleStep, cx, cy, maxR]);

  if (n === 0) return null;

  const rings = [0.25, 0.5, 0.75, 1];

  return (
    <svg width={totalSize} height={totalSize} viewBox={`0 0 ${totalSize} ${totalSize}`} style={{ display: 'block' }}>
      {/* grille */}
      {rings.map(r => (
        <polygon
          key={r}
          points={toPointsAttr(axes.map((_, i) => polarPoint(cx, cy, maxR * r, i * angleStep)))}
          fill="none"
          stroke={gridColor}
          strokeWidth={1}
        />
      ))}

      {/* rayons */}
      {axes.map((axis, i) => {
        const p = polarPoint(cx, cy, maxR, i * angleStep);
        return <line key={axis.id} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={gridColor} strokeWidth={1} />;
      })}

      {/* position de comparaison (ex. archétype le plus proche) — pointillé, sans remplissage */}
      {comparePoints && (
        <polygon
          points={toPointsAttr(comparePoints)}
          fill="none"
          stroke={compareColor}
          strokeWidth={1.5}
          strokeDasharray="3 3"
          opacity={0.9}
        />
      )}

      {/* position réelle — pleine */}
      <polygon
        points={toPointsAttr(mainPoints)}
        fill={fillColor}
        fillOpacity={0.16}
        stroke={fillColor}
        strokeWidth={2}
      />
      {mainPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={2.5} fill={fillColor} />
      ))}

      {/* libellés d'axes */}
      {axes.map((axis, i) => {
        const p = polarPoint(cx, cy, labelR, i * angleStep);
        const anchor = Math.abs(p.x - cx) < 4 ? 'middle' : (p.x > cx ? 'start' : 'end');
        return (
          <text
            key={axis.id}
            x={p.x}
            y={p.y}
            textAnchor={anchor}
            dominantBaseline="middle"
            fontFamily='"JetBrains Mono", "Fira Code", monospace'
            fontSize={9}
            letterSpacing="0.06em"
            fill={labelColor}
          >
            {(axis.shortLabel || axis.id).toUpperCase()}
          </text>
        );
      })}
    </svg>
  );
};

export default AxisRadarChart;